// @flow
import React, { useContext, useEffect } from 'react';
import queryString from 'query-string';
import { Redirect, withRouter } from 'react-router-dom';

import Box from 'bizom-mui/lib/Box';
import BlankSlate from 'bizom-mui/lib/BlankSlate';
import ProgressBlock from 'bizom-mui/lib/ProgressBlock';

import ErrorIcon from '@material-ui/icons/Error';

import { HOME } from 'constants/paths';
import { SessionContext } from 'session';
import {
  UNKNOWN_ERROR,
  SOMETHING_WENT_WRONG,
  WRONG_APP_IDENTIFIER,
  INSUFFICIENT_DATA_PROVIDED,
  USER_DETAILS_ARE_NOT_VALID,
  AUTHENTICATION_TOKEN_NOT_VALID,
  AUTHENTICATION_TOKEN_HAS_EXPIRED,
} from 'constants/errors';

import type { RemoteLoginParams } from 'types/session.js.flow';

type RemoteLoginProps = {
  loading: boolean,
  location: any,
  remoteLogin: (RemoteLoginParams) => Promise<any>,
};

const getErrorMessage = (params, error) => {
  const xRetKey = process.env.REACT_APP_RETAILER_APP_X_RET_KEY || '';

  if (!params || !params.tk || !params.ai || !params.ph || !params.dc) {
    return INSUFFICIENT_DATA_PROVIDED;
  }

  if (params.ai !== xRetKey) {
    return WRONG_APP_IDENTIFIER;
  }

  if (error && error.message) {
    if (error.message.indexOf('Invalid JWT') !== -1) {
      return AUTHENTICATION_TOKEN_NOT_VALID;
    }
    if (error.message.indexOf('Token is expired') !== -1) {
      return AUTHENTICATION_TOKEN_HAS_EXPIRED;
    }
    if (error.message.indexOf('Invalid credentials provided') !== -1) {
      return USER_DETAILS_ARE_NOT_VALID;
    }
    return UNKNOWN_ERROR;
  }

  return null;
};

const RemoteAuth = ({
  location,
  loading,
  remoteLogin,
}: RemoteLoginProps): any => {
  const { error, session } = useContext(SessionContext);
  const params = queryString.parse(location.search) || {};
  const errorMessage = getErrorMessage(params, error);

  useEffect(() => {
    if (session && !!session.token) return;

    if (!errorMessage) {
      remoteLogin({
        token: params.tk,
        xRetKey: params.ai,
        phoneno: params.ph,
        dialCode: params.dc,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (session && session.token) {
    return <Redirect to={HOME} />;
  }

  if (!!errorMessage) {
    return (
      <Box mt={8}>
        <BlankSlate
          dense
          title={SOMETHING_WENT_WRONG}
          subtitle={errorMessage}
          image={<ErrorIcon fontSize="large" color="error" />}
        />
      </Box>
    );
  }

  return <ProgressBlock />;
};

export default withRouter(RemoteAuth);
